import {
  SITE_NAME,
  SITE_URL,
  isLandingSeoIndexable,
  landingPath,
  landingSeoKey,
  type LandingSection,
} from '@/app/shared/landing/pages/landing-route-seo';
import { landingSeo } from '@/app/shared/landing/pages/landing-seo-data';

/**
 * Structured data for the landing pages, mirroring what the source marketing site emits:
 * a Service description, a BreadcrumbList, and a FAQPage wherever the page has an FAQ.
 *
 * The FAQ block is rendered by `SubPageFaq` from the same array it displays, so the markup
 * and the structured data can never drift apart, the usual failure mode is someone
 * editing a question and forgetting the duplicated schema.
 */

const abs = (path: string) => `${SITE_URL}${path}`;

function JsonLd({ data }: { data: unknown }) {
  return (
    <script
      type="application/ld+json"
      // Schema payloads are built from our own typed data, never user input.
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
}

/**
 * Section index routes do not exist, so the breadcrumb uses the shortest truthful path.
 * Every non-final Google breadcrumb must have an `item` URL; emitting an unlinked section
 * between Home and the page makes the entire BreadcrumbList invalid.
 */
export function LandingBreadcrumbJsonLd({
  locale,
  section,
  slug,
}: {
  locale: string;
  section: LandingSection;
  slug: string;
}) {
  const entry = landingSeo[landingSeoKey(section, slug)];
  const items = [
    { name: 'Home', item: abs(`/${locale}`) },
    {
      name: entry?.title ?? slug,
      item: abs(landingPath(locale, section, slug)),
    },
  ];

  return (
    <JsonLd
      data={{
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: items.map((entryItem, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: entryItem.name,
          item: entryItem.item,
        })),
      }}
    />
  );
}

/** Emitted only for routes whose SEO entry carries the Service fields. */
export function LandingServiceJsonLd({
  locale,
  section,
  slug,
}: {
  locale: string;
  section: LandingSection;
  slug: string;
}) {
  const entry = landingSeo[landingSeoKey(section, slug)];
  if (!entry?.serviceType) return null;

  return (
    <JsonLd
      data={{
        '@context': 'https://schema.org',
        '@type': 'Service',
        serviceType: entry.serviceType,
        name: entry.serviceName ?? entry.title,
        description: entry.serviceDescription ?? entry.description,
        url: abs(landingPath(locale, section, slug)),
        provider: {
          '@type': 'Organization',
          name: SITE_NAME,
          url: abs(`/${locale}`),
        },
        ...(entry.areaServed === false
          ? {}
          : {
              areaServed: {
                '@type': 'Country',
                name: entry.areaServed ?? 'United States',
              },
            }),
      }}
    />
  );
}

export function LandingFaqJsonLd({
  items,
}: {
  items: readonly (readonly [question: string, answer: string])[];
}) {
  if (!items.length) return null;

  return (
    <JsonLd
      data={{
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: items.map(([question, answer]) => ({
          '@type': 'Question',
          name: question,
          acceptedAnswer: { '@type': 'Answer', text: answer },
        })),
      }}
    />
  );
}

/** Convenience wrapper: the two page-level schemas every sub-page should carry. */
export function LandingPageJsonLd(props: {
  locale: string;
  section: LandingSection;
  slug: string;
}) {
  const entry = landingSeo[landingSeoKey(props.section, props.slug)];

  // Do not describe unfinished/noindex pages as complete public resources.
  if (!entry || !isLandingSeoIndexable(entry)) return null;

  return (
    <>
      <LandingServiceJsonLd {...props} />
      <LandingBreadcrumbJsonLd {...props} />
    </>
  );
}
